import { addressDisplay } from "@/shared/lib/formatting";
import type { Holding } from "@/admin/types/products";

export interface HoldingConsumerFields {
  uuid: string;
  firstName: string;
  lastName: string;
  fullName: string;
  email: string;
  phone: string;
  /** Display string for the consumer's address; "" when none is on file. */
  address: string;
  /** Label sourced from the ADDRESS attribute, falling back to "Address". */
  addressLabel: string;
}

/**
 * Single resolver for the customer/consumer card display, shared by the
 * registration and claim detail pages. Both pages read the SAME Holding object
 * (via useHolding), so the customer fields - including the address - must
 * resolve identically. The address prefers the consumer's generic ADDRESS EAV
 * attribute and falls back to the holding's mailing address; it never reads
 * phantom columns off ConsumerAccount (which has no address columns), so it
 * cannot silently resolve to an empty "-" the way a flat serializer field can.
 */
export function resolveHoldingConsumerFields(
  holding: Pick<Holding, "consumer_account" | "mailing_address">,
): HoldingConsumerFields {
  const consumer = holding.consumer_account;
  const firstName = consumer?.first_name ?? "";
  const lastName = consumer?.last_name ?? "";
  const addressAttr = consumer?.attribute_values?.find(
    (a) => a.field_type === "ADDRESS",
  );
  const address = addressAttr
    ? addressDisplay(addressAttr.value)
    : (holding.mailing_address?.display ?? "");
  return {
    uuid: consumer?.uuid ?? "",
    firstName,
    lastName,
    fullName: [firstName, lastName].filter(Boolean).join(" "),
    email: consumer?.email ?? "",
    phone: consumer?.phone_number ?? "",
    address,
    addressLabel: addressAttr?.display_name || "Address",
  };
}
